import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { type AppDispatch, type RootState } from "../../../Application/globaltypes/redux";
import cn from "classnames";
import { type ColumnOption } from "../../../../interfaces/columnOptions";
import {
  ItemsTypes,
  PublishedStatusTypes,
  RolePermissions,
  RouteNames,
  type SortingDirection,
  ViewType,
} from "../../../../enums";
import GenericItemsView from "views/ItemsView/GenericItemsView";
import { type FiltersMap } from "utils/filterUtils";
import { SearchInput } from "components";
import { useNavigate } from "react-router";
import { PublishStatus } from "components/common/PublishStatus";
import dateTimeUtils from "utils/dateTimeUtils";
import { type ContentResponse } from "features/Application/services/dataServices/contentDataService";
import {
  setSearch,
  resetSearch,
  setPagination,
  resetPagination,
  setSortingColumnName as setFilterSortingColumnName,
  setSortingDirection as setFilterSortingDirection,
  setAppliedFilter,
  resetFilter as resetAppliedFilter,
  resetSortingColumnName,
  resetSortingDirection,
  resetShouldSort,
  setShouldSort,
} from "../state/ExternalLinksFiltersSlice";
import { setSelected } from "../state/ExternalLinksOverviewSlice";
import { HandleOnSelectionChanged, type SelectionChangedArgs } from "interfaces/onSelectionChanged";
import { ExternalLinksThreeDotsItemMenu } from "./ExternalLinksThreeDotsItemMenu";
import { AssetVisibilityConfirmationModal } from "components/assetVisibilityConfirmationModal/AssetVisibilityConfirmationModal";
import DeleteContentConfirmationModal, {
  type RemoveContentObserverInput,
} from "components/deleteContentConfirmationModal/DeleteContentConfirmationModal";
import { ContentTypesEnum } from "enums";
import { useObservable } from "hooks/useObservable";
import { bindAction } from "interfaces";
import {
  changeExternalLinkVisibility,
  deleteExternalLinks,
  duplicateExternalLinks,
  lockExternalLink,
} from "../state/thunks/externalLinkCommandThunk";
import { isEmpty } from "lodash";
import { ExternalLinksOverviewHeader } from "./ExternalLinksOverviewHeader";
import { useRtn } from "hooks/useRtn";
import {
  DeleteAllLinkSuccess,
  DuplicateAllLinkSuccess,
  LinkEditVisibilityAllSuccess,
  LinkLockSuccess,
} from "features/Application/services/realTimeNotification/events/library/libraryEvents";
import { HiddenContent } from "components/HiddenContent/HiddenContent";
import { Title } from "components/listViewTemplates";
import { useHasPermissions } from "features/Application/hooks";
import { fetchExternalLinks, getOverviewFilterOptions } from "../state/thunks/externalLinkQueriesThunk";
import { type Filters } from "utils/queryUtils";
import { ExternalLinksNoResults } from "./ExternalLinksNoResults";
import { CreateExternalLinkButton } from "./CreateExternalLinkButton";
import { LinkFilterForm } from "components/filterForms";

const sortColumnLookup = (columnName: string | undefined) => {
  switch (columnName) {
    case "title":
      return "Title";
    case "modified":
      return "DateModified";
    case "added":
      return "DateCreated";
    case "status":
      return "IsDraft";
    default:
      return "";
  }
};

const ExternalLinksOverview: FC<PropsFromRedux> = ({
  links,
  totalCount,
  isLoading,
  areAllLoaded,
  fetchLinks,
  filters,
  setSearch,
  setPagination,
  setSortingColumnName: setFilterSortingColumnName,
  setSortingDirection: setFilterSortingDirection,
  setAppliedFilter,
  resetFilter: resetAppliedFilter,
  selected,
  setSelected,
  setShouldSort,
  deleteLinks,
  lockExternalLink,
  changeExternalLinkVisibility,
  duplicateExternalLinks,
  getFilterOptions,
  hardReset,
}) => {
  const navigate = useNavigate();
  const reloadListItemsRef = useRef<(enableSorting: boolean) => void>();
  const [purchasedSelectedCount, setPurchasedSelectedCount] = useState(0);
  const [hiddenSelectedCount, setHiddenSelectedCount] = useState(0);
  const [visibleSelectedCount, setVisibleSelectedCount] = useState(0);
  const [draftSelectedCount, setDraftSelectedCount] = useState(0);
  const [unpublishedSelectedCount, setUnpublishedSelectedCount] = useState(0);
  const hasCreatePermission = useHasPermissions([RolePermissions.AssetsCreate]);

  useEffect(() => {
    return () => {
      if (!window.location.pathname.includes(`/${RouteNames.contentLinks}`)) hardReset();
    };
  }, [hardReset]);

  const deleteObserver =
    useObservable<(onConfirm: () => void, removeContentInput: RemoveContentObserverInput) => void>();
  const changeVisibilityObserver = useObservable<(onConfirm: () => void, hidden: boolean, ids: number[]) => void>();

  const getExternalLinksData = (
    skip: number,
    top: number,
    sortingColumnName?: string,
    sortDirection?: SortingDirection,
  ) => {
    fetchLinks(
      skip,
      top,
      sortColumnLookup(sortingColumnName ?? filters.sortingColumnName),
      sortDirection ?? filters.sortingDirection,
    );
    setShouldSort(true);
  };

  const clearSelection = useCallback(() => {
    setSelected([]);
    setPurchasedSelectedCount(0);
    setHiddenSelectedCount(0);
    setVisibleSelectedCount(0);
    setDraftSelectedCount(0);
  }, [setSelected]);

  const onSelectionChanged = useCallback(
    (args: SelectionChangedArgs<ContentResponse>) => {
      let purchasedCount = 0;
      let draftCount = 0;
      let hiddenCount = 0;
      let visibleCount = 0;
      let unpublishedCount = 0;

      const onAdded = (link: ContentResponse) => {
        link.isPurchased && ++purchasedCount;
        link.isDraft && ++draftCount;
        !link.visibility && ++hiddenCount;
        link.visibility && ++visibleCount;
        !link.hasBeenPublished && ++unpublishedCount;
      };
      const onRemoved = (link: ContentResponse) => {
        link.isPurchased && --purchasedCount;
        link.isDraft && --draftCount;
        !link.visibility && --hiddenCount;
        link.visibility && --visibleCount;
        !link.hasBeenPublished && --unpublishedCount;
      };

      HandleOnSelectionChanged(args, onAdded, onRemoved, clearSelection);

      setPurchasedSelectedCount((p) => p + purchasedCount);
      setDraftSelectedCount((p) => p + draftCount);
      setHiddenSelectedCount((p) => p + hiddenCount);
      setVisibleSelectedCount((p) => p + visibleCount);
      setUnpublishedSelectedCount((p) => p + unpublishedCount);
    },
    [clearSelection],
  );
  const goToEditEntity = useCallback(
    (payload: { id: number }) => navigate(`${payload.id.toString()}/configuration`),
    [navigate],
  );

  const handleEdit = useCallback(
    (id: number, isDraft: boolean) => {
      isDraft ? goToEditEntity({ id }) : lockExternalLink(id);
    },
    [goToEditEntity, lockExternalLink],
  );
  const refetchItemsFromStart = useCallback(() => {
    reloadListItemsRef.current?.(isEmpty(filters.search));
  }, [reloadListItemsRef, filters.search]);

  const refreshItemsAfterAction = useCallback(() => {
    setTimeout(() => {
      reloadListItemsRef.current?.(false);
    }, 500);
  }, [reloadListItemsRef]);

  useRtn([LinkLockSuccess], goToEditEntity);
  useRtn([DeleteAllLinkSuccess, DuplicateAllLinkSuccess, LinkEditVisibilityAllSuccess], refreshItemsAfterAction);

  const handleDelete = useCallback(
    (ids: number[], flowsCount?: number, packsCount?: number) => {
      deleteObserver.notify(
        () => {
          deleteLinks(ids, refetchItemsFromStart);
          clearSelection();
        },
        {
          selectedItemsCount: ids.length,
          flowsCount,
          packsCount,
        },
      );
    },
    [clearSelection, deleteLinks, deleteObserver, refetchItemsFromStart],
  );

  const handleToggleVisibility = useCallback(
    (ids: number[], hidden: boolean) => () => {
      changeVisibilityObserver.notify(
        () => {
          changeExternalLinkVisibility(ids, hidden, refetchItemsFromStart);
          clearSelection();
        },
        hidden,
        ids,
      );
    },
    [changeVisibilityObserver, clearSelection, refetchItemsFromStart, changeExternalLinkVisibility],
  );

  const handleDuplicate = useCallback(
    (ids: number[]) => {
      clearSelection();
      duplicateExternalLinks(ids, refetchItemsFromStart);
    },
    [duplicateExternalLinks, refetchItemsFromStart, clearSelection],
  );

  const applyNewFilter = useCallback(
    (newFilter: Filters) => {
      clearSelection();
      setAppliedFilter(newFilter);
    },
    [clearSelection, setAppliedFilter],
  );

  const handleSearchChange = useCallback(
    (search: string) => {
      setSearch(search);
      setShouldSort(search === "");
      setTimeout(() => {
        refetchItemsFromStart();
      }, 0);
    },
    [setSearch, setShouldSort, refetchItemsFromStart],
  );

  const getColumnOptions = (): Array<ColumnOption<ContentResponse>> => [
    {
      name: "Title",
      isSortable: true,
      width: 7,
      render: (item: ContentResponse) => (
        <HiddenContent variant="list" isHidden={!item.visibility} showIcon>
          <Title
            url={`${item.id}`}
            title={item.title}
            clamp={2}
            className={cn("normal-color", { "hidden-content": !item.visibility })}
          />
        </HiddenContent>
      ),
    },
    {
      name: "Status",
      width: 3,
      isSortable: true,
      render: (item: ContentResponse) => (
        <HiddenContent variant="list" isHidden={!item.visibility}>
          <PublishStatus
            publishedStatus={PublishedStatusTypes.ConvertToPublishedStatusType(!item.isDraft, item.hasBeenPublished)}
          />
        </HiddenContent>
      ),
    },
    {
      name: "Modified",
      isSortable: true,
      width: 2,
      render: (item: ContentResponse) => (
        <HiddenContent variant="list" isHidden={!item.visibility}>
          {dateTimeUtils.formatDate(item.dateModified)}
        </HiddenContent>
      ),
    },
    {
      name: "Added",
      isSortable: true,
      width: 2,
      render: (item: ContentResponse) => (
        <HiddenContent variant="list" isHidden={!item.visibility}>
          {dateTimeUtils.formatDate(item.dateCreated)}
        </HiddenContent>
      ),
    },
    {
      name: "",
      width: 1,
      className: "align-right",
      isSortable: false,
      render: (item: ContentResponse, isChecked) => (
        <ExternalLinksThreeDotsItemMenu
          circle
          outlinedEllipsis
          item={item}
          isChecked={isChecked}
          readonly={!hasCreatePermission}
          hasPermission
          hideDisabled={!hasCreatePermission}
          handleEdit={handleEdit}
          handleDelete={handleDelete}
          handleDuplicate={handleDuplicate}
          handleToggleVisibility={handleToggleVisibility}
        />
      ),
    },
  ];

  return (
    <section className="nested-content external-links-overview">
      <ExternalLinksOverviewHeader
        selectedIds={selected}
        purchasedSelected={purchasedSelectedCount > 0}
        draftSelected={draftSelectedCount > 0}
        hiddenSelected={hiddenSelectedCount > 0}
        visibleSelected={visibleSelectedCount > 0}
        unpublishedSelected={unpublishedSelectedCount > 0}
        clearSelection={clearSelection}
        handleDuplicate={handleDuplicate}
        handleDelete={handleDelete}
        handleToggleVisibility={handleToggleVisibility}
      />
      <GenericItemsView
        className="alignment-padding"
        blur
        renderSearch={() => (
          <SearchInput placeholder="Search for Links..." onChange={handleSearchChange} defaultValue={filters.search} />
        )}
        // @ts-ignore
        getFilterForm={() => <LinkFilterForm />}
        getFilterOptions={getFilterOptions}
        filterOptions={filters.filterOptions}
        filterOptionsLoading={filters.isLoading}
        appliedFilter={filters.appliedFilter}
        applyFilter={applyNewFilter}
        resetFilter={() => resetAppliedFilter()}
        noResultsContent={
          <ExternalLinksNoResults
            filtered={!isEmpty(filters.appliedFilter) || !isEmpty(filters.search)}
            createButton={<CreateExternalLinkButton />}
          />
        }
        pagination={filters.pagination}
        setPagination={setPagination}
        isSelectDisabled={() => !hasCreatePermission}
        items={links}
        itemsType={ItemsTypes.Link}
        dataCount={totalCount}
        isLoading={isLoading}
        isAllDataLoaded={areAllLoaded}
        fetchData={getExternalLinksData}
        setSortingColumnName={setFilterSortingColumnName}
        setSortingDirection={setFilterSortingDirection}
        defaultSortingColumnName={filters.shouldSort ? filters.sortingColumnName : ""}
        sortingDirection={filters.shouldSort ? filters.sortingDirection : undefined}
        columnOptions={getColumnOptions()}
        viewType={ViewType.LIST}
        selectedIds={selected}
        onSelectedItemChanged={setSelected}
        onSelectionChanged={onSelectionChanged}
        setReloadListItems={(reloadListItems) => (reloadListItemsRef.current = reloadListItems)}
      />
      <DeleteContentConfirmationModal
        contentType={ContentTypesEnum.Links}
        onTriggerRemoveContentObserver={deleteObserver}
      />
      <AssetVisibilityConfirmationModal observer={changeVisibilityObserver} assetType="link" />
    </section>
  );
};

const mapStateToProps = (state: RootState) => ({
  links: state.library.externalLinks.overview.items,
  totalCount: state.library.externalLinks.overview.itemsCount,
  isLoading: state.library.externalLinks.overview.isLoading,
  areAllLoaded: state.library.externalLinks.overview.areAllLoaded,
  filters: state.library.externalLinks.filters,
  selected: state.library.externalLinks.overview.selected,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  fetchLinks: bindAction(fetchExternalLinks, dispatch),
  setSearch: (search: string) => dispatch(setSearch(search)),
  setPagination: (pagination: number) => dispatch(setPagination(pagination)),
  setSortingColumnName: (columnName: string) => dispatch(setFilterSortingColumnName(columnName)),
  setSortingDirection: (direction: SortingDirection) => dispatch(setFilterSortingDirection(direction)),
  setAppliedFilter: (filter: FiltersMap) => dispatch(setAppliedFilter(filter)),
  resetFilter: () => dispatch(resetAppliedFilter()),
  hardReset: () => {
    dispatch(resetSearch());
    dispatch(resetAppliedFilter());
    dispatch(resetPagination());
    dispatch(resetSortingColumnName());
    dispatch(resetSortingDirection());
    dispatch(resetShouldSort());
  },
  setSelected: (selectedIds: number[]) => dispatch(setSelected(selectedIds)),
  setShouldSort: bindAction(setShouldSort, dispatch),
  deleteLinks: bindAction(deleteExternalLinks, dispatch),
  lockExternalLink: bindAction(lockExternalLink, dispatch),
  changeExternalLinkVisibility: bindAction(changeExternalLinkVisibility, dispatch),
  duplicateExternalLinks: bindAction(duplicateExternalLinks, dispatch),
  getFilterOptions: bindAction(getOverviewFilterOptions, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(ExternalLinksOverview);

export default ConnectedComponent;
